import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth.jsx';
import { api } from '../api.js';
import { useI18n } from '../i18n/index.jsx';
import ConversationList from './ConversationList.jsx';
import ChatView from './ChatView.jsx';
import { useChat, useChatEvents, notificationsOn, setNotifications } from './ChatProvider.jsx';
import NewMessage from './NewMessage.jsx';
import Icon from '../components/Icon.jsx';
import { Avatar } from './ConversationList.jsx';
import './chat.css';
import TutorialSlot from '../tutorials/TutorialSlot.jsx';

// The chat screen: conversation list on the left, the open conversation on
// the right. On phones they are two screens (list, then conversation with
// a back button). Member: base '/chat', link '/account/messages'. Team:
// base '/admin/chat', link '/admin/messages' (support inbox, or every
// conversation for super admins with scope 'all').
// apiBase '/workspace/chat': a PME representative (the team view, their own conversations).
export default function MessagesScreen({ team = false, scope = 'support', linkBase, apiBase = null }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, errorText } = useI18n();
  const { mode } = useChat();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [notify, setNotify] = useState(notificationsOn());
  const [newMsg, setNewMsg] = useState(false);
  const [online, setOnline] = useState(null); // team: members online now
  useEffect(() => {
    if (!team || apiBase) return undefined;
    const load2 = () => api('/admin/chat/online').then(setOnline).catch(() => {});
    load2();
    const tm = setInterval(load2, 30 * 1000);
    return () => clearInterval(tm);
  }, [team, apiBase]);
  async function writeTo(userId) {
    try { const r = await api('/admin/chat/start', { method: 'POST', body: { userId } }); navigate(`/admin/messages/${r.id}`); } catch (e) { setError(errorText(e)); }
  }
  const base = apiBase || (team ? '/admin/chat' : '/chat');
  const timer = useRef(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ filter, ...(q ? { q } : {}), ...(team && !apiBase ? { scope } : {}) });
    return api(`${base}/conversations?${qs}`).then((r) => { setItems(r.conversations); setError(''); }).catch((e) => setError(errorText(e)));
  }, [base, filter, q, team, scope, errorText]);
  useEffect(() => { clearTimeout(timer.current); timer.current = setTimeout(load, q ? 250 : 0); return () => clearTimeout(timer.current); }, [load, q]);
  useChatEvents((type) => { if (type === 'message' || type === 'read') { clearTimeout(timer.current); timer.current = setTimeout(load, 300); } });

  // Phones: the conversation takes the whole height above the keyboard.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const set = () => document.documentElement.style.setProperty('--chat-vh', `${vv.height}px`);
    set(); vv.addEventListener('resize', set);
    return () => { vv.removeEventListener('resize', set); document.documentElement.style.removeProperty('--chat-vh'); };
  }, []);

  async function openSupport() {
    try { const r = await api('/chat/conversations', { method: 'POST', body: { support: true } }); navigate(`${linkBase}/${r.id}`); } catch (e) { setError(errorText(e)); }
  }
  const changed = (what) => {
    load();
    if (what === 'archived' || what === 'deleted') { setToast(t(`chat.done.${what}`)); navigate(linkBase); setTimeout(() => setToast(''), 3000); }
  };
  const toggleNotify = async () => setNotify(await setNotifications(!notify));

  return (
    <div className={`chat${id ? ' has-conv' : ''}${team ? ' chat-team' : ''}`}>
      <aside className="chat-side">
        <div className="chat-side-head">
          <h1>{apiBase ? t('chat.repTitle') : team ? (scope === 'all' ? t('chat.adminAll') : t('chat.adminTitle')) : t('chat.title')}</h1>
          <span className={`chat-mode chat-mode-${mode}`} title={t(`chat.mode.${mode}`)}>{t(`chat.modeShort.${mode}`)}</span>
          <TutorialSlot place={apiBase ? 'ws.teamchat' : team ? 'adm.messages' : 'ws.messages'} />
        </div>
        {team && !apiBase && user?.isSuperAdmin && (
          <nav className="chat-filters chat-scope" aria-label={t('chat.adminScope')}>
            <NavLink to="/admin/messages" className={({ isActive }) => (isActive ? 'on' : '')}>{t('chat.adminTitle')}</NavLink>
            <NavLink to="/admin/moderation" className={({ isActive }) => (isActive ? 'on' : '')}>{t('chat.adminAll')}</NavLink>
          </nav>
        )}
        {!team && <button type="button" className="btn btn-gold btn-sm chat-new-btn" onClick={() => setNewMsg(true)}><Icon name="plus" size={16} /> {t('chat.newMessage')}</button>}
        {team && online && (
          <details className="chat-online-box">
            <summary>● {t('chat.onlineNow', { n: online.length })}</summary>
            {online.length > 0 && <ul>{online.map((m) => <li key={m.id}><button type="button" onClick={() => writeTo(m.id)} title={t('chat.adminWrite')}><Avatar person={m} size={26} status={{ online: true }} /> <span>{m.company || m.name}</span></button></li>)}</ul>}
          </details>
        )}
        {'Notification' in window && (
          <label className="chat-notify small"><input type="checkbox" checked={notify} onChange={toggleNotify} /> {t('chat.browserNotify')}</label>
        )}
        {error && <p className="field-error small">{error}</p>}
        {toast && <p className="chat-toast small" role="status">{toast}</p>}
        <ConversationList items={items} loading={!items} activeId={id} base={linkBase} filter={filter} setFilter={setFilter} q={q} setQ={setQ}
          team={team} pinSupport={!team} onSupport={openSupport} />
      </aside>
      <section className="chat-main">
        {id ? <ChatView key={id} convId={id} base={base} team={team} backTo={linkBase} onChanged={changed} />
          : <div className="chat-empty"><span className="chat-empty-icon" aria-hidden="true">💬</span><p className="strong">{t('chat.pickTitle')}</p><p className="muted small">{team ? t('chat.pickTeam') : t('chat.pick')}</p></div>}
      </section>
      {newMsg && <NewMessage onClose={() => setNewMsg(false)} />}
    </div>
  );
}
